import { StepType } from '@reactour/tour';
import { Box } from 'components/mui';
import TourStepContent from '../TourStepContent';
import SandboxTourNote from '../SandboxTourNote';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, clickThroughMask, sidebarMenuStep } from '../stepStyles';

// Step indices for the "Add a user" tour. Menu steps (OPEN_CANAPPS, USERS_ITEM)
// auto-advance via useSidebarTourControl; NEW_USER / VIEW_FULL / SAVE advance via
// useAddUserTourSync; the field steps in between are Next-driven.
export const ADD_USER_STEP = {
	WELCOME: 0,
	OPEN_CANAPPS: 1,
	USERS_ITEM: 2,
	NEW_USER: 3,
	VIEW_FULL: 4,
	FIRST_NAME: 5,
	LAST_NAME: 6,
	COMPANIES: 7,
	USERNAME: 8,
	PASSWORD: 9,
	CONFIRM_PASSWORD: 10,
	ROLE_GROUP: 11,
	SAVE: 12,
	FINISH: 13,
} as const;

const steps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		stepInteraction: false,
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Add a new user 👤"
				body={
					<>
						<Box sx={{ mb: 1.25 }}>
							Let’s create a user and give them access.
						</Box>
						<Box sx={{ mb: 1.25 }}>
							As an <strong>admin</strong>, you can invite new users
							to your CANapps instance.
						</Box>
						<Box>
							Each user can be given different roles — like{' '}
							<strong>CANpark guards</strong> or{' '}
							<strong>CANmeet members</strong> — that decide what they
							can see and do.
						</Box>
						<SandboxTourNote>
							<strong>Note:</strong> This is a sandbox walkthrough—nothing
							you enter is saved.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
	{
		selector: '[data-tour="canapps-menu-group"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="canapps-menu-group"]'),
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				body="Open the CANapps menu — click it to expand."
			/>
		),
	},
	{
		selector: '[data-tour="users-menu-item"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="users-menu-item"]'),
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="right"
				body="Now click “Users”."
			/>
		),
	},
	{
		selector: '[data-tour="new-user-button"]',
		position: ({ left, bottom }) => [left, bottom + 12],
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="bottom"
				tailOffset={-44}
				body="Click “NEW USER” to open the user form."
			/>
		),
	},
	{
		selector: '[data-tour="user-view-toggle"]',
		// The toggle sits near the top-left, so the default centered 'bottom' box
		// gets shoved right to stay on-screen and the tail drifts off it. Left-align
		// the box under the toggle instead; the negative tailOffset then anchors the
		// tail near the box's left so it points back at the toggle's centre.
		position: ({ left, bottom }) => [left, bottom + 12],
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="bottom"
				tailOffset={-44}
				body="Switch to “Full” to see the complete set of fields."
			/>
		),
	},
	{
		selector: '[data-tour="user-first-name"]',
		position: 'right',
		content: (
			<TourStepContent hideBack tailPosition="right" body="Enter the user’s first name." />
		),
	},
	{
		selector: '[data-tour="user-last-name"]',
		position: 'right',
		content: (
			<TourStepContent tailPosition="right" body="And their last name." />
		),
	},
	{
		selector: '[data-tour="user-companies"]',
		position: 'right',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				tailPosition="right"
				body="Pick the companies this user belongs to from the dropdown."
			/>
		),
	},
	{
		selector: '[data-tour="user-username"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				body="Set the username — this is the email they’ll sign in with."
			/>
		),
	},
	{
		selector: '[data-tour="user-password"]',
		position: 'left',
		content: (
			<TourStepContent tailPosition="left" body="Give them a password." />
		),
	},
	{
		selector: '[data-tour="user-confirm-password"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				body="Re-enter the same password to confirm it."
			/>
		),
	},
	{
		selector: '[data-tour="user-role-group"]',
		position: 'left',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				tailPosition="left"
				body="Roles decide what this user can see and do. Use this dropdown to assign one or more role groups — each grants access to specific apps and permissions."
			/>
		),
	},
	{
		selector: '[data-tour="user-save"]',
		// Save sits in the crowded bottom-right of a scrollable panel (Reset right
		// beside it), so a box next to it gets shoved around on smaller screens.
		// Put it ABOVE Save, right-aligned to Save's right edge so it stays on
		// screen, and point the tail down at the button.
		position: ({ right, top }, prevRect) => [
			right - prevRect.width,
			top - prevRect.height - 12,
		],
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				tailOffset={30}
				body="All set—click “Save” to complete the walkthrough. No user will be created."
			/>
		),
	},
	{
		selector: 'body',
		position: 'center',
		stepInteraction: false,
		styles: { maskWrapper: (base) => ({ ...base, opacity: 0 }) },
		content: (
			<TourStepContent
				hideBack
				title="Walkthrough complete! 🎉"
				body={
					<>
						<span>
							That’s how you add a user and assign their roles in CANapps.
						</span>
						<SandboxTourNote>
							<strong>Sandbox reminder:</strong> No user was created and
							nothing you entered was saved.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
];

export const canappsAddUserTour: TourConfig = {
	steps,
	startRoute: '/',
	allowedRoutes: ['/', '/users'],
};
